import cx from 'classnames';
import { useEffect, useState } from 'react';
import './message.scss';

export interface IMessageProps {
  className?: string;
  message: string;
  description?: string;
  variant?: 'alert' | 'notice' | 'success' | 'info';
  urgency?: 'default' | 'important' | 'minor';
  onClose?: () => void;
  autoHide?: number;
}

const Message: React.FC<IMessageProps> = ({
  className,
  message,
  description,
  variant = 'notice',
  urgency = 'default',
  onClose,
  autoHide
}) => {
  useEffect(() => {
    if (autoHide) {
      const timer = setTimeout(() => {
        onClose?.();
      }, autoHide + 100); // +100ms to allow CSS transition to finish
      return () => clearTimeout(timer);
    }
  }, [autoHide, onClose]);
  return (
    <div
      className={cx('message alert tw:relative', className, {
        'alert-danger': variant === 'alert',
        'alert-warning': variant === 'notice',
        'alert-success': variant === 'success',
        'alert-info': variant === 'info',
        'alert-important': urgency === 'important',
        'alert-minor': urgency === 'minor',
        'alert-dismissible': !!onClose
      })}
    >
      <div>
        <h4 className="alert-heading">{message}</h4>
        {description && <div className="alert-description">{description}</div>}
      </div>
      {onClose && <a className="btn-close" aria-label="close" onClick={onClose}></a>}

      {autoHide != null && <AutoProgress duration={autoHide} variant={variant} />}
    </div>
  );
};

const AutoProgress: React.FC<{ duration: number; variant: IMessageProps['variant'] }> = ({ duration, variant }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    setProgress(0); // animation is in css
  }, [duration]);

  return (
    <div
      className="progress progress-sm"
      role="progressbar"
      style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}
      data-progress-duration={duration}
    >
      <div
        className={cx('progress-bar', {
          'bg-success': variant === 'success',
          'bg-danger': variant === 'alert',
          'bg-warning': variant === 'notice',
          'bg-info': variant === 'info'
        })}
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
};

export default Message;
